import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon } from 'lucide-react';

interface BookingCalendarProps {
    selectedDate: string; // YYYY-MM-DD
    onDateSelect: (date: string) => void;
    minDate?: string; // YYYY-MM-DD
}

export const BookingCalendar: React.FC<BookingCalendarProps> = ({ selectedDate, onDateSelect, minDate }) => {
    const [viewDate, setViewDate] = useState(new Date());

    const daysInMonth = (year: number, month: number) => new Date(year, month + 1, 0).getDate();
    const firstDayOfMonth = (year: number, month: number) => new Date(year, month, 1).getDay();

    const handleDateClick = (day: number) => {
        const year = viewDate.getFullYear();
        const month = String(viewDate.getMonth() + 1).padStart(2, '0');
        const date = String(day).padStart(2, '0');
        onDateSelect(`${year}-${month}-${date}`);
    };

    const prevMonth = () => {
        setViewDate(new Date(viewDate.setMonth(viewDate.getMonth() - 1)));
    };

    const nextMonth = () => {
        setViewDate(new Date(viewDate.setMonth(viewDate.getMonth() + 1)));
    };

    const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

    const days = daysInMonth(viewDate.getFullYear(), viewDate.getMonth());
    const startOffset = firstDayOfMonth(viewDate.getFullYear(), viewDate.getMonth());

    return (
        <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-sm w-full max-w-md">
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <div className="bg-blue-600 p-2 rounded-xl text-white">
                        <CalendarIcon size={18} />
                    </div>
                    <h3 className="text-sm font-black text-slate-800 uppercase tracking-tight">
                        {monthNames[viewDate.getMonth()]} {viewDate.getFullYear()}
                    </h3>
                </div>
                <div className="flex gap-1">
                    <button onClick={prevMonth} className="p-2 hover:bg-slate-100 rounded-lg transition-all border border-slate-100">
                        <ChevronLeft size={16} className="text-slate-600" />
                    </button>
                    <button onClick={nextMonth} className="p-2 hover:bg-slate-100 rounded-lg transition-all border border-slate-100">
                        <ChevronRight size={16} className="text-slate-600" />
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-7 gap-px bg-slate-100 rounded-2xl overflow-hidden border border-slate-100">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                    <div key={d} className="bg-slate-50 p-2 text-[8px] font-black uppercase text-slate-400 text-center border-b border-slate-100">
                        {d}
                    </div>
                ))}
                {Array(startOffset).fill(null).map((_, i) => (
                    <div key={`empty-${i}`} className="bg-slate-50/50 h-12" />
                ))}
                {Array(days).fill(null).map((_, i) => {
                    const day = i + 1;
                    const dateStr = `${viewDate.getFullYear()}-${String(viewDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                    const isSelected = selectedDate === dateStr;
                    const isToday = new Date().toISOString().slice(0, 10) === dateStr;
                    const isDisabled = minDate && dateStr < minDate;

                    return (
                        <div 
                            key={day} 
                            onClick={() => !isDisabled && handleDateClick(day)}
                            className={`h-12 p-2 relative cursor-pointer transition-all text-center flex items-center justify-center
                                ${isSelected ? 'bg-blue-600 text-white font-bold' : 
                                  isDisabled ? 'bg-slate-50 text-slate-300 cursor-not-allowed' : 
                                  'bg-white text-slate-600 hover:bg-blue-50'} 
                                ${isToday && !isSelected && !isDisabled ? 'text-blue-600 font-bold ring-1 ring-inset ring-blue-600' : ''}
                            `}
                        >
                            <span className="text-xs tabular-nums">{day}</span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
